import pipe from '../utils/pipe.js';
import YouTubeAPI from '../core/YouTubeAPI.js';

let activePlayer = null;

document.body.addEventListener('modalLaunch', function () {
    if (activePlayer) {
        activePlayer.player.pauseVideo();
    }
});

const PLAYER_STATES = {
    disabled: -3,
    loading: -2,
    unstarted: -1,
    ended: 0,
    playing: 1,
    paused: 2,
    buffering: 3,
    cued: 5
};

const PLAYERS = [];

const registerPlayer = function (playerStruct) {
    playerStruct.playerIndex = PLAYERS.length;
    PLAYERS.push(playerStruct);
    return playerStruct;
};

const createPlayerStruct = function (playerOpts) {
    return {
        state: playerOpts.state,
        videoId: parseVideoId(playerOpts.el.dataset.youtubeUrl),
        dom: {
            el: playerOpts.el,
            songEl: playerOpts.songEl,
            control: playerOpts.el.querySelector('.video-player-control'),
            embed: playerOpts.el.querySelector('.player-embed'),
            overlay: playerOpts.el.querySelector('.video-player-overlay'),
            playNext: playerOpts.el.querySelector('.play-next-video'),
            playPrev: playerOpts.el.querySelector('.play-prev-video')
        },
        player: null,
        playerIndex: null,
        mouseMoveTimeout: {
            id: null,
            delay: 1000
        },
        play () {
            handleControlClick(this);
        }
    };
};

const parseVideoId = function (youtubeUrl = '') {
    const videoIdMatch = youtubeUrl.match(/https?:\/\/youtu\.be\/(.*?)$/);
    return videoIdMatch ? videoIdMatch[1] : null;
};

const bindStateListeners = function (playerStruct) {
    const {
        state
    } = playerStruct;

    state.onChange(() => { updateUIState(playerStruct); });

    return playerStruct;
};

const bindDomEvents = function (playerStruct) {
    const {
        dom
    } = playerStruct;

    dom.overlay.onmousemove = () => { handleOverlayMouseMove(playerStruct); };
    dom.control.onclick = () => { handleControlClick(playerStruct); };
    dom.control.onmouseover = () => { handleControlMouseOver(playerStruct); };
    dom.control.onmouseout = () => { handleControlMouseOut(playerStruct); };
    dom.playNext.onclick = () => { playNextVideo(playerStruct); };
    dom.playPrev.onclick = () => { playPrevVideo(playerStruct); };

    return playerStruct;
};

const setInitialState = function (playerStruct) {
    const {
        videoId
    } = playerStruct;

    if (!videoId) {
        updatePlayerState(playerStruct, PLAYER_STATES.disabled);
    }

    return playerStruct;
};

const updatePlayerState = function (playerStruct, newStateCode) {
    const {
        state
    } = playerStruct;

    for (let stateKey in PLAYER_STATES) {
        let stateKeyCode = PLAYER_STATES[stateKey];
        if (newStateCode === stateKeyCode) {
            state.code = newStateCode;
            state.key = stateKey;
            break;
        }
    };

    updateActivePlayer(playerStruct, newStateCode);
};

const updateActivePlayer = function (playerStruct, stateCode) {
    if (
        activePlayer !== playerStruct &&
        (stateCode === PLAYER_STATES.playing || stateCode === PLAYER_STATES.loading)
    ) {
        if (activePlayer) {
            quickStop(activePlayer);
        }
        activePlayer = playerStruct;
    }
};

const updateUIState = function (playerStruct) {
    const {
        dom,
        state,
        playerIndex
    } = playerStruct;

    dom.el.setAttribute('data-player-state', state.key);
    dom.el.style.height = null;

    if (playerIndex === 0) {
        dom.playPrev.classList.add('state-hidden');
    } else if (playerIndex === PLAYERS.length - 1) {
        dom.playNext.classList.add('state-hidden');
    }

    switch (state.code) {
    case PLAYER_STATES.playing:
    case PLAYER_STATES.loading:
        setTimeout(() => {
            scrollToPlayer(playerStruct);
        }, 100);
        break;
    }
};

const scrollToPlayer = function (playerStruct) {
    const {
        dom
    } = playerStruct;

    const elBounds = dom.songEl.getBoundingClientRect();
    scrollTo(window.scrollY + elBounds.top, 500);
};

const scrollTo = function (to, duration) {
    if (duration <= 0) return;
    let difference = to - window.scrollY;
    let perTick = difference / duration * 10;

    setTimeout(function () {
        window.scrollTo(0, window.scrollY + perTick);
        if (window.scrollY === to) return;
        scrollTo(to, duration - 10);
    }, 10);
};

const createPlayer = function (playerStruct) {
    const {
        videoId,
        dom
    } = playerStruct;

    const {
        width,
        height
    } = getPlayerDimensions(playerStruct);

    playerStruct.player = YouTubeAPI.createPlayer(dom.embed, {
        videoId,
        width,
        height,
        events: {
            onReady () {
                playerStruct.player.playVideo();
            },
            onStateChange (state) {
                updatePlayerState(playerStruct, state.data);
            }
        }
    });

    updatePlayerState(playerStruct, PLAYER_STATES.loading);

    return playerStruct;
};

const getPlayerDimensions = function (playerStruct) {
    const {
        dom
    } = playerStruct;

    const width = dom.el.offsetWidth;
    const height = width * (9 / 16);

    return {
        width,
        height
    };
};

const handleControlClick = function (playerStruct) {
    const {
        player
    } = playerStruct;

    if (!player) {
        createPlayer(playerStruct);
    } else {
        togglePlayerState(playerStruct);
    }
};

const handleOverlayMouseMove = function (playerStruct) {
    const {
        mouseMoveTimeout,
        dom
    } = playerStruct;

    if (mouseMoveTimeout.id) {
        clearTimeout(mouseMoveTimeout.id);
    }

    dom.el.classList.add('show-controls');
    mouseMoveTimeout.id = setTimeout(() => {
        dom.el.classList.remove('show-controls');
    }, mouseMoveTimeout.delay);
};

const handleControlMouseOver = function (playerStruct) {
    const {
        mouseMoveTimeout
    } = playerStruct;
    mouseMoveTimeout.delay = 3000;
};

const handleControlMouseOut = function (playerStruct) {
    const {
        mouseMoveTimeout
    } = playerStruct;
    mouseMoveTimeout.delay = 1000;
};

const togglePlayerState = function (playerStruct) {
    const {
        player
    } = playerStruct;
    const playerState = player.getPlayerState();

    switch (playerState) {
    case PLAYER_STATES.playing:
        player.pauseVideo();
        break;
    default:
        player.playVideo();
        break;
    }
};

const quickStop = function (playerStruct) {
    const {
        dom,
        player
    } = playerStruct;

    dom.el.classList.add('no-transition');
    dom.songEl.classList.add('no-transition');
    player.pauseVideo();
    setTimeout(() => {
        dom.el.classList.remove('no-transition');
        dom.songEl.classList.remove('no-transition');
    }, 100);
};

const playPrevVideo = function (playerStruct) {
    const prevPlayer = PLAYERS[playerStruct.playerIndex - 1];
    if (prevPlayer) {
        handleControlClick(prevPlayer);
    }
};

const playNextVideo = function (playerStruct) {
    const nextPlayer = PLAYERS[playerStruct.playerIndex + 1];
    if (nextPlayer) {
        handleControlClick(nextPlayer);
    }
};

const VideoPlayer = function (playerOpts) {
    let playerStruct = createPlayerStruct(playerOpts);

    playerStruct = pipe(playerStruct, [
        bindStateListeners,
        bindDomEvents,
        setInitialState,
        registerPlayer
    ]);

    return playerStruct;
};

export default VideoPlayer;



// WEBPACK FOOTER //
// ./assets/js/components/VideoPlayer.js